<script lang="ts">
  import { tick } from 'svelte';
  import { ApiError, fileUrl, thumbUrl, type Attachment, type Message } from '../lib/api';
  import { emoji } from '../lib/emoji';
  import { formatSize, formatTime } from '../lib/format';
  import { mentions, render } from '../lib/markdown';
  import { router, threadPath } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';
  import EmojiPicker from './EmojiPicker.svelte';
  import TaskCard from './TaskCard.svelte';

  let {
    message,
    grouped = false,
    inThread = false,
    archived = false,
    highlighted = false,
  }: { message: Message; grouped?: boolean; inThread?: boolean; archived?: boolean; highlighted?: boolean } = $props();

  let picking = $state(false);
  let editing = $state(false);
  let draft = $state('');
  let error = $state('');
  let editor: HTMLTextAreaElement | undefined = $state();

  const me = $derived(app.me?.username ?? '');
  const mine = $derived(message.author.id === app.me?.id);
  const html = $derived(message.deleted ? '' : render(message.body, me));
  const mentionsMe = $derived(!message.deleted && !mine && !!me && mentions(message.body, me));
  const actionable = $derived(!message.deleted && message.kind !== 'system');

  async function run(action: () => Promise<void>) {
    error = '';
    try {
      await action();
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    }
  }

  const react = (name: string) => {
    picking = false;
    void run(() => app.toggleReaction(message, name));
  };

  async function startEdit() {
    draft = message.body;
    editing = true;
    await tick();
    editor?.focus();
    editor?.setSelectionRange(draft.length, draft.length);
  }

  const saveEdit = () =>
    run(async () => {
      if (draft.trim() && draft !== message.body) await app.edit(message, draft);
      editing = false;
    });

  function onEditKey(event: KeyboardEvent) {
    if (event.key === 'Escape') editing = false;
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      void saveEdit();
    }
  }

  /** Colour of the file badge, by family. */
  function family(file: Attachment): string {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (file.mime === 'application/pdf') return 'pdf';
    if (file.mime.startsWith('image/')) return 'image';
    if (['doc', 'docx', 'odt', 'rtf', 'md', 'txt'].includes(ext)) return 'doc';
    if (['xls', 'xlsx', 'ods', 'csv'].includes(ext)) return 'sheet';
    if (['zip', '7z', 'gz', 'tar', 'rar'].includes(ext)) return 'archive';
    return 'other';
  }
  const opens = (file: Attachment) => file.mime === 'application/pdf' || file.mime.startsWith('image/');
  /** Files the right panel can show: Markdown, text, PDF. */
  const previewable = (file: Attachment) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return ['text/markdown', 'text/plain', 'application/pdf'].includes(file.mime) || ['md', 'markdown', 'txt'].includes(ext);
  };
  const images = $derived(message.attachments.filter((f) => f.has_thumb));
  const others = $derived(message.attachments.filter((f) => !f.has_thumb));

  const remove = () => {
    if (confirm('Supprimer ce message ? Cette action est définitive.')) void run(() => app.remove(message));
  };
</script>

{#if message.kind === 'system'}
  <p class="system" data-message-id={message.id}>
    {message.body}<span aria-hidden="true">{' · '}</span><time datetime={message.created_at}>{formatTime(message.created_at)}</time>
  </p>
{:else}
  <article
    class="message"
    class:grouped
    class:mention={mentionsMe}
    class:highlighted
    data-message-id={message.id}
    aria-label="Message de {message.author.display_name}"
  >
    {#if actionable && !editing}
      <div class="toolbar" role="toolbar" aria-label="Actions sur le message" class:open={picking}>
        {#if !archived}
          <button type="button" aria-label="Réagir" title="Réagir" onclick={() => (picking = !picking)}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9" /><path d="M8.5 14a4 4 0 007 0M9 9.5h.01M15 9.5h.01" /></svg>
          </button>
        {/if}
        {#if !message.root_id && !inThread && !archived}
          <button type="button" aria-label="Répondre dans le fil" title="Répondre dans le fil" onclick={() => router.go(threadPath(message.conversation_id, message.id))}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a8 8 0 01-11.6 7.1L4 20l1-4.6A8 8 0 1121 12z" /></svg>
          </button>
        {/if}
        <button
          type="button"
          aria-label={message.saved ? 'Retirer des messages enregistrés' : 'Enregistrer'}
          title={message.saved ? 'Retirer des messages enregistrés' : 'Enregistrer'}
          aria-pressed={message.saved}
          onclick={() => run(() => app.toggleSaved(message))}
        >
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill={message.saved ? 'currentColor' : 'none'} stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M6 3h12v18l-6-4-6 4z" /></svg>
        </button>
        {#if !archived}
          <button
            type="button"
            aria-label={message.pinned ? 'Désépingler' : 'Épingler'}
            title={message.pinned ? 'Désépingler' : 'Épingler'}
            aria-pressed={message.pinned}
            onclick={() => run(() => app.togglePin(message))}
          >
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill={message.pinned ? 'currentColor' : 'none'} stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M9 3h6l-1 6 4 4H6l4-4zM12 13v8" /></svg>
          </button>
        {/if}
        {#if mine && !archived}
          <button type="button" aria-label="Modifier" title="Modifier" onclick={startEdit}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4L19 9l-4-4L4 16zM14 6l4 4" /></svg>
          </button>
          <button type="button" class="danger" aria-label="Supprimer" title="Supprimer" onclick={remove}>
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>
          </button>
        {/if}
        {#if picking}
          <EmojiPicker onPick={react} onClose={() => (picking = false)} />
        {/if}
      </div>
    {/if}

    <div class="gutter">
      {#if grouped}
        <time class="hover-time" datetime={message.created_at}>{formatTime(message.created_at)}</time>
      {:else}
        <Avatar user={message.author} size={36} />
      {/if}
    </div>
    <div class="content">
      {#if !grouped}
        <header>
          <span class="author">{message.author.display_name}</span>
          {#if message.kind === 'task'}<span class="created-task">a créé une tâche</span>{/if}
          <time datetime={message.created_at}>{formatTime(message.created_at)}</time>
          {#if message.pinned}
            <span class="pinned">
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M9 3h6l-1 6 4 4H6l4-4zM11 13h2v8h-2z" /></svg>
              {message.pinned_by ? `Épinglé par ${message.pinned_by.display_name}` : 'Épinglé'}
            </span>
          {/if}
          {#if message.saved}
            <span class="saved" title="Enregistré">
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6 3h12v18l-6-4-6 4z" /></svg>
              <span class="sr-only">Enregistré</span>
            </span>
          {/if}
        </header>
      {:else if message.pinned}
        <span class="pinned">{message.pinned_by ? `Épinglé par ${message.pinned_by.display_name}` : 'Épinglé'}</span>
      {/if}

      {#if message.deleted}
        <p class="deleted">Message supprimé</p>
      {:else if editing}
        <div class="edit">
          <label class="sr-only" for="edit-{message.id}">Modifier le message</label>
          <textarea id="edit-{message.id}" bind:this={editor} bind:value={draft} rows="3" onkeydown={onEditKey}></textarea>
          <div class="edit-actions">
            <span class="hint">Échap pour annuler, Entrée pour enregistrer</span>
            <button type="button" class="btn" onclick={() => (editing = false)}>Annuler</button>
            <button type="button" class="btn primary" onclick={saveEdit}>Enregistrer</button>
          </div>
        </div>
      {:else if message.kind === 'task' && message.task}
        <TaskCard {message} task={message.task} />
      {:else}
        <!-- Markdown rendered without raw HTML (markdown-it, html: false). -->
        <div class="body">{@html html}</div>
        {#if message.edited_at}<span class="edited">(modifié)</span>{/if}
      {/if}

      {#if images.length > 0 && message.kind !== 'task'}
        <ul class="images">
          {#each images as file (file.id)}
            <li>
              <a
                href={fileUrl(file.id)}
                title="{file.name} · {formatSize(file.size_bytes)}"
                onclick={(e) => {
                  // A plain click shows it in the panel; Ctrl+click still opens a tab.
                  if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  app.showPreview(message, file);
                }}
              >
                <img src={thumbUrl(file.id)} alt={file.name} loading="lazy" />
              </a>
            </li>
          {/each}
        </ul>
      {/if}
      {#if others.length > 0 && message.kind !== 'task'}
        <ul class="files">
          {#each others as file (file.id)}
            <li class="file-line">
              <!-- PDF and images open in a tab; everything else is downloaded. -->
              <a
                class="file"
                href={fileUrl(file.id)}
                target={opens(file) ? '_blank' : undefined}
                rel="noopener"
                download={opens(file) ? undefined : file.name}
                title="{opens(file) ? 'Ouvrir' : 'Télécharger'} {file.name}"
              >
                <span class="ext {family(file)}" aria-hidden="true">{file.name.includes('.') ? file.name.split('.').pop()?.slice(0, 4).toUpperCase() : 'FICH'}</span>
                <span class="file-text"><span class="file-name">{file.name}</span><span class="file-size">{formatSize(file.size_bytes)}</span></span>
                <svg class="download" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v12M7 11l5 5 5-5M5 20h14" /></svg>
              </a>
              {#if previewable(file)}
                <button type="button" class="preview-btn" onclick={() => app.showPreview(message, file)} aria-label="Aperçu de {file.name}">Aperçu</button>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}

      {#if message.reactions.length > 0}
        <ul class="reactions" aria-label="Réactions">
          {#each message.reactions as r (r.emoji)}
            <li>
              <button
                type="button"
                class="reaction"
                class:me={r.me}
                aria-pressed={r.me}
                disabled={archived || message.deleted}
                title=":{r.emoji}: · {r.user_ids
                  .map((id) => app.conversation(message.conversation_id)?.members.find((m) => m.id === id)?.display_name ?? '?')
                  .join(', ')}"
                onclick={() => react(r.emoji)}
              >
                {emoji(r.emoji)} {r.count}
              </button>
            </li>
          {/each}
        </ul>
      {/if}

      {#if message.reply_count > 0 && !inThread}
        <a
          class="thread"
          href={threadPath(message.conversation_id, message.id)}
          onclick={(e) => {
            e.preventDefault();
            router.go(threadPath(message.conversation_id, message.id));
          }}
        >
          {message.reply_count}
          {message.kind === 'task' ? 'commentaire' : 'réponse'}{message.reply_count > 1 ? 's' : ''}{message.last_reply_at
            ? ` · dernière à ${formatTime(message.last_reply_at)}`
            : ''}
        </a>
      {/if}
      {#if error}<p class="error" role="alert">{error}</p>{/if}
    </div>
  </article>
{/if}

<style>
  .message {
    position: relative;
    display: flex;
    gap: 12px;
    padding: 6px 12px;
    border-radius: var(--radius-m);
  }
  .message:not(.grouped) {
    margin-top: 6px;
  }
  .message.grouped {
    padding-top: 1px;
    padding-bottom: 1px;
  }
  .message:hover,
  .message:focus-within {
    background: var(--bg-hover);
  }
  .message.mention {
    background: var(--mention-bg);
    box-shadow: inset 3px 0 0 var(--mention-bar);
  }
  .message.highlighted {
    animation: flash 2s ease-out;
  }
  @keyframes flash {
    from {
      background: var(--accent-selected);
    }
  }
  .toolbar {
    position: absolute;
    top: -14px;
    right: 12px;
    z-index: 5;
    display: none;
    gap: 2px;
    padding: 2px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    box-shadow: 0 2px 6px rgba(16, 24, 40, 0.08);
  }
  .message:hover .toolbar,
  .message:focus-within .toolbar,
  .toolbar.open {
    display: flex;
  }
  .toolbar button {
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    color: var(--muted);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .toolbar button:hover {
    background: var(--bg-hover);
    color: var(--ink);
  }
  .toolbar button[aria-pressed='true'] {
    background: var(--accent-soft);
    color: var(--accent);
  }
  .toolbar button.danger:hover {
    color: var(--danger);
    background: var(--danger-soft);
  }
  .gutter {
    flex: 0 0 36px;
    display: flex;
    justify-content: center;
  }
  .hover-time {
    visibility: hidden;
    font-size: 11px;
    color: var(--muted);
    line-height: 22px;
  }
  .message:hover .hover-time {
    visibility: visible;
  }
  .content {
    flex: 1;
    min-width: 0;
  }
  header {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }
  .author {
    font-weight: 600;
  }
  .created-task {
    font-size: 12px;
    color: var(--muted);
  }
  time {
    font-size: 12px;
    color: var(--muted);
  }
  .pinned {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--accent-on-soft);
    font-weight: 500;
  }
  .saved {
    color: var(--accent);
    display: inline-flex;
  }
  .body {
    overflow-wrap: anywhere;
  }
  .body :global(p) {
    margin: 0;
  }
  .body :global(p + p),
  .body :global(ul),
  .body :global(ol),
  .body :global(pre),
  .body :global(blockquote) {
    margin: 6px 0 0;
  }
  .body :global(ul),
  .body :global(ol) {
    padding-left: 20px;
  }
  .body :global(code) {
    font-family: var(--font-mono);
    font-size: 13px;
    background: var(--bg-page);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 0 4px;
  }
  .body :global(pre) {
    padding: 10px 12px;
    background: #1a1d21;
    color: #e8eaed;
    border-radius: var(--radius-m);
    overflow-x: auto;
  }
  .body :global(pre code) {
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
  }
  .body :global(blockquote) {
    padding-left: 12px;
    border-left: 3px solid var(--border-strong);
    color: var(--ink-2);
  }
  .body :global(.mention) {
    color: var(--accent-text);
    font-weight: 600;
  }
  .body :global(.mention.mine) {
    background: var(--accent-soft);
    border-radius: 4px;
    padding: 0 2px;
  }
  .body :global(img) {
    max-width: 100%;
  }
  .edited {
    font-size: 12px;
    color: var(--muted);
  }
  .deleted {
    margin: 0;
    color: var(--muted);
    font-style: italic;
  }
  .edit {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .edit textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--accent);
    border-radius: var(--radius-m);
    background: var(--bg);
    resize: vertical;
    outline: none;
  }
  .edit-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .edit-actions .hint {
    flex: 1;
    font-size: 12px;
    color: var(--muted);
  }
  .edit-actions .btn {
    height: 32px;
  }
  .files {
    list-style: none;
    margin: 6px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .images {
    list-style: none;
    margin: 6px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .images li {
    max-width: 100%;
  }
  .images a {
    display: inline-block;
    max-width: 100%;
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    overflow: hidden;
    line-height: 0;
  }
  .images img {
    display: block;
    width: auto;
    max-width: 320px;
    max-height: 240px;
    background: var(--bg-page);
  }
  @media (max-width: 759px) {
    .images img {
      max-width: 100%;
    }
  }
  .file {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-m);
    max-width: 320px;
    color: inherit;
    text-decoration: none;
    background: var(--bg);
  }
  .file:hover {
    border-color: var(--border-strong);
  }
  .file-line {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
  }
  .preview-btn {
    height: 30px;
    padding: 0 10px;
    border: 1px solid var(--border);
    border-radius: 15px;
    background: var(--bg);
    color: var(--accent-text);
    font-size: 13px;
    font-weight: 600;
  }
  .preview-btn:hover {
    border-color: var(--accent-border);
    background: var(--accent-soft);
  }
  .download {
    flex: 0 0 auto;
    color: var(--muted);
  }
  .file:hover .download {
    color: var(--accent-text);
  }
  .ext {
    flex: 0 0 36px;
    height: 36px;
    border-radius: var(--radius-s);
    background: var(--bg-page);
    color: var(--ink-2);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ext.pdf {
    background: var(--danger-soft);
    color: var(--danger);
  }
  .ext.doc {
    background: var(--status-doing-soft);
    color: var(--status-doing-text);
  }
  .ext.sheet {
    background: var(--status-done-soft);
    color: var(--status-done);
  }
  .ext.image {
    background: var(--accent-soft);
    color: var(--accent-on-soft);
  }
  .ext.archive {
    background: var(--status-todo-soft);
    color: var(--status-todo-text);
  }
  .file-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .file-name {
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .file-size {
    font-size: 12px;
    color: var(--muted);
  }
  .reactions {
    list-style: none;
    margin: 6px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .reaction {
    height: 26px;
    padding: 0 8px;
    border: 1px solid var(--border);
    border-radius: 13px;
    background: var(--bg);
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .reaction:hover:not(:disabled) {
    border-color: var(--border-strong);
  }
  .reaction.me {
    border-color: var(--accent-border);
    background: var(--accent-soft);
  }
  .reaction:disabled {
    cursor: default;
  }
  .thread {
    display: inline-block;
    margin-top: 4px;
    font-size: 13px;
    color: var(--accent-text);
    font-weight: 600;
    text-decoration: none;
  }
  .thread:hover {
    text-decoration: underline;
  }
  .error {
    margin-top: 6px;
    font-size: 13px;
  }
  .system {
    margin: 4px 0;
    padding: 0 12px 0 60px;
    font-size: 13px;
    color: var(--muted);
  }
  .system time {
    font-size: 12px;
  }
  @media (hover: none) {
    /* On touch screens the toolbar shows when the message is tapped (focus-within). */
    .toolbar button {
      width: 40px;
      height: 40px;
    }
  }
</style>
